import type { ShoppingBulkImportResult, ShoppingCategory, ShoppingItem, ShoppingItemInput, ShoppingList, ShoppingListSummary, ShoppingSuggestion } from "../../shared/shopping-list";

async function apiFetch(path: string, init?: RequestInit): Promise<Response> {
  const headers = new Headers(init?.headers);
  headers.set("accept", "application/json");
  if (init?.body !== undefined) {
    headers.set("content-type", "application/json");
  }
  return fetch(path, { ...init, headers });
}

async function readJsonResponse<T>(response: Response): Promise<T> {
  const body: unknown = await response.json();
  return body as T;
}

export async function fetchShoppingLists(): Promise<ShoppingListSummary[]> {
  const response = await apiFetch("/api/shopping-lists");
  if (!response.ok) {
    throw new Error("shopping_lists_fetch_failed");
  }
  const body = await readJsonResponse<{ lists: ShoppingListSummary[] }>(response);
  return body.lists;
}

export async function createShoppingList(name: string): Promise<ShoppingList> {
  const response = await apiFetch("/api/shopping-lists", { method: "POST", body: JSON.stringify({ name }) });
  if (!response.ok) {
    throw new Error("shopping_list_create_failed");
  }
  const body = await readJsonResponse<{ list: ShoppingList }>(response);
  return body.list;
}

export async function fetchShoppingList(listId: string): Promise<ShoppingList> {
  const response = await apiFetch(`/api/shopping-lists/${listId}`);
  if (!response.ok) {
    throw new Error("shopping_list_fetch_failed");
  }
  const body = await readJsonResponse<{ list: ShoppingList }>(response);
  return body.list;
}

export async function addShoppingItem(listId: string, input: ShoppingItemInput): Promise<ShoppingItem> {
  const response = await apiFetch(`/api/shopping-lists/${listId}/items`, { method: "POST", body: JSON.stringify(input) });
  if (!response.ok) {
    throw new Error("shopping_item_add_failed");
  }
  const body = await readJsonResponse<{ item: ShoppingItem }>(response);
  return body.item;
}

export async function bulkImportShoppingItems(listId: string, text: string): Promise<ShoppingBulkImportResult> {
  const response = await apiFetch(`/api/shopping-lists/${listId}/items/bulk-import`, {
    method: "POST",
    body: JSON.stringify({ text, clientMutationId: crypto.randomUUID() })
  });
  if (!response.ok) {
    throw new Error("shopping_bulk_import_failed");
  }
  return readJsonResponse<ShoppingBulkImportResult>(response);
}

export async function completeShoppingItem(listId: string, itemId: string, completed: boolean): Promise<ShoppingItem> {
  const response = await apiFetch(`/api/shopping-lists/${listId}/items/${itemId}/complete`, {
    method: "POST",
    body: JSON.stringify({ completed })
  });
  if (!response.ok) {
    throw new Error("shopping_item_complete_failed");
  }
  const body = await readJsonResponse<{ item: ShoppingItem }>(response);
  return body.item;
}

export async function clearCompletedShoppingItems(listId: string): Promise<number> {
  const response = await apiFetch(`/api/shopping-lists/${listId}/clear-completed`, { method: "POST" });
  if (!response.ok) {
    throw new Error("shopping_clear_completed_failed");
  }
  const body = await readJsonResponse<{ cleared: number }>(response);
  return body.cleared;
}

export async function reorderShoppingCategories(listId: string, orderedCategoryIds: readonly string[]): Promise<void> {
  const response = await apiFetch(`/api/shopping-lists/${listId}/categories/reorder`, {
    method: "POST",
    body: JSON.stringify({ orderedCategoryIds })
  });
  if (!response.ok) {
    throw new Error("shopping_category_reorder_failed");
  }
}

export async function deleteShoppingCategory(listId: string, categoryId: string): Promise<ShoppingCategory[]> {
  const response = await apiFetch(`/api/shopping-lists/${listId}/categories/${categoryId}`, { method: "DELETE" });
  if (!response.ok) {
    throw new Error("shopping_category_delete_failed");
  }
  const body = await readJsonResponse<{ categories: ShoppingCategory[] }>(response);
  return body.categories;
}

export async function suggestShoppingItems(query: string): Promise<ShoppingSuggestion[]> {
  const response = await apiFetch(`/api/shopping-lists/suggestions?q=${encodeURIComponent(query)}`);
  if (!response.ok) {
    return [];
  }
  const body = await readJsonResponse<{ suggestions: ShoppingSuggestion[] }>(response);
  return body.suggestions;
}
